import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT, useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ChevronLeft, Calendar, Video, MapPin, UserRound, MessageSquare, Clock, Info, Mail, Phone } from "lucide-react";
import { initials } from "@/lib/format";

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useT();
  const { locale } = useI18n();

  const [data, setData] = useState(undefined);
  const [note, setNote] = useState("");
  const [savedAt, setSavedAt] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { data: d } = await api.get(`/coach/offerings/bookings/${id}`);
      setData(d);
      setNote(d?.booking?.client_note || "");
      setSavedAt(d?.booking?.client_note_updated_at || "");
    } catch { setData(null); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const isClient = data?.client?.id === user?.id;

  const saveNote = async () => {
    setSaving(true);
    try {
      if (!note.trim()) {
        await api.delete(`/coach/offerings/bookings/${id}/client-note`);
        setSavedAt("");
      } else {
        const { data: r } = await api.put(`/coach/offerings/bookings/${id}/client-note`, { note: note.trim() });
        setSavedAt(r?.client_note_updated_at || new Date().toISOString());
      }
      toast.success(t("bkc.saved"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (data === undefined) return <Spinner label={t("common.loading")} />;

  if (data === null) {
    return (
      <div className="space-y-4">
        <Back t={t} navigate={navigate} />
        <p className="text-sm text-muted-foreground">{t("bkc.session_not_found")}</p>
      </div>
    );
  }

  const offering = data.offering || {};
  const coach = data.coach || {};
  const scheduled = data.booking?.scheduled_at ? new Date(data.booking.scheduled_at) : null;
  const whenText = scheduled
    ? scheduled.toLocaleString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : t("bkc.time_tba");
  const inPerson = offering.delivery_mode === "in_person";

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="session-detail-screen">
      <Back t={t} navigate={navigate} />

      <div className="rounded-2xl border border-primary/20 bg-accent/40 p-5">
        <h1 className="font-display text-2xl text-primary">{offering.title || t("bkc.session_title")}</h1>
        {offering.duration_minutes ? (
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-3.5 w-3.5" /> {t("bkc.duration_min", { n: offering.duration_minutes })}
          </p>
        ) : null}
      </div>

      <Card icon={Calendar} title={t("bkc.when")}>
        <p className="text-base text-foreground">{whenText}</p>
      </Card>

      <Card icon={inPerson ? MapPin : Video} title={t("bkc.where")}>
        {inPerson ? (
          <>
            <p className="text-base text-foreground">{offering.location || t("bkc.location_tba")}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t("bkc.in_person_hint")}</p>
          </>
        ) : (
          <>
            <p className="text-base text-foreground">{t("bkc.virtual")}</p>
            {offering.meeting_link ? (
              <Button className="mt-3 gap-1.5 rounded-full" size="sm" onClick={() => window.open(offering.meeting_link, "_blank")} data-testid="session-open-meeting">
                <Video className="h-4 w-4" /> {t("bkc.join_meeting")}
              </Button>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">{t("bkc.link_tba")}</p>
            )}
          </>
        )}
      </Card>

      {isClient && coach?.name ? (
        <Card icon={UserRound} title={t("bkc.your_coach")}>
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              {coach.photo ? <AvatarImage src={fileUrl(coach.photo)} alt={coach.name} /> : null}
              <AvatarFallback>{initials(coach.name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-medium text-foreground">{coach.name}</p>
              {coach.bio ? <p className="line-clamp-2 text-xs text-muted-foreground">{coach.bio}</p> : null}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {coach.email ? (
              <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => window.open(`mailto:${coach.email}`)} data-testid="session-coach-email">
                <Mail className="h-3.5 w-3.5" /> {t("bkc.email")}
              </Button>
            ) : null}
            {coach.phone ? (
              <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => window.open(`tel:${coach.phone}`)} data-testid="session-coach-phone">
                <Phone className="h-3.5 w-3.5" /> {t("bkc.phone")}
              </Button>
            ) : null}
          </div>
        </Card>
      ) : null}

      <Card icon={MessageSquare} title={t("bkc.note_title")}>
        <p className="mb-2 text-xs text-muted-foreground">{t("bkc.note_hint")}</p>
        {isClient ? (
          <>
            <Textarea
              data-testid="session-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("bkc.note_ph")}
              maxLength={4000}
              className="min-h-[120px]"
            />
            {savedAt ? <p className="mt-2 text-xs italic text-muted-foreground">{t("bkc.note_saved_at", { when: new Date(savedAt).toLocaleString(locale) })}</p> : null}
            <Button className="mt-3 gap-1.5 rounded-full" size="sm" disabled={saving} onClick={saveNote} data-testid="session-note-save">
              {saving ? t("common.saving") : (note ? t("bkc.save") : t("bkc.clear"))}
            </Button>
          </>
        ) : (
          <p className="text-sm text-foreground">{note || "—"}</p>
        )}
      </Card>

      <div className="flex items-start gap-2 rounded-2xl bg-accent/40 p-4">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">{t("bkc.cancel_policy", { pct: Math.round(data.cancellation_policy?.refund_pct || 0) })}</p>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{title}</p>
      </div>
      {children}
    </div>
  );
}

function Back({ t, navigate }) {
  return (
    <button onClick={() => navigate("/app/bookings")} data-testid="session-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ChevronLeft className="h-4 w-4" /> {t("bkc.bookings_title")}
    </button>
  );
}
